import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import {
  Button,
  ConnectionIndicator,
  Input,
  RadixSelect as Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  useToast,
} from '@/components/ui';
import { useEventBuffer } from '../../hooks/useEventBuffer';
import { useEventGrouping, type GroupMode } from './useEventGrouping';
import EventCard from './EventCard';
import RunGroupCard from './RunGroupCard';
import SourceGroupCard from './SourceGroupCard';

const EXCLUDE_CHIPS: { label: string; patterns: string[] }[] = [
  { label: 'Deltas', patterns: ['agent.run.reasoning_delta', 'agent.run.response_delta', 'agent.run.final_answer_delta'] },
  { label: 'Entity events', patterns: ['entity.'] },
  { label: 'KB events', patterns: ['kb.'] },
  { label: 'Email events', patterns: ['email.processing.', 'entity.conn-email.'] },
  { label: 'Surface events', patterns: ['agent.run.surface_update', 'agent.run.canvas_created', 'agent.run.surface_deleted'] },
  { label: 'Integration events', patterns: ['com.', 'integration.event.failed'] },
];

const DISCONNECT_TOAST_DELAY_MS = 3000;

export default function EventStreamPage() {
  const {
    events,
    totalEvents,
    groupCount,
    maxGroupedItems,
    clearEvents,
    sourceFilter,
    setSourceFilter,
    eventTypeFilter,
    setEventTypeFilter,
    isConnected,
    diagnostics,
  } = useEventBuffer();

  const toast = useToast();
  const prevConnectedRef = useRef(isConnected);
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showedDisconnectRef = useRef(false);

  const [groupMode, setGroupMode] = useState<GroupMode>('run');
  const [excludedTypes, setExcludedTypes] = useState<Set<string>>(
    new Set(['agent.run.reasoning_delta', 'agent.run.response_delta', 'agent.run.final_answer_delta'])
  );

  useEffect(() => {
    if (prevConnectedRef.current && !isConnected) {
      // Delay disconnect toast — quick reconnects produce zero toasts
      disconnectTimerRef.current = setTimeout(() => {
        disconnectTimerRef.current = null;
        showedDisconnectRef.current = true;
        toast.warning('Event stream reconnecting...');
      }, DISCONNECT_TOAST_DELAY_MS);
    } else if (!prevConnectedRef.current && isConnected) {
      if (disconnectTimerRef.current) {
        // Reconnected before toast fired — suppress entirely
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      } else if (showedDisconnectRef.current) {
        toast.success('Event stream connected.');
      }
      showedDisconnectRef.current = false;
    }
    prevConnectedRef.current = isConnected;
  }, [isConnected, toast]);

  useEffect(() => {
    return () => {
      if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
    };
  }, []);

  // Collect unique sources from the full (unfiltered-by-source) buffer
  const uniqueSources = useMemo(() => {
    const sources = new Set<string>();
    for (const e of events) {
      if (e.source) sources.add(e.source);
    }
    return Array.from(sources).sort();
  }, [events]);

  const items = useEventGrouping(events, { groupMode, excludedTypes });

  function toggleExclude(patterns: string[]) {
    setExcludedTypes((prev) => {
      const next = new Set(prev);
      const allExcluded = patterns.every((p) => next.has(p));
      for (const p of patterns) {
        if (allExcluded) {
          next.delete(p);
        } else {
          next.add(p);
        }
      }
      return next;
    });
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Event Stream</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Monitor real-time events across your organization
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        {/* Source filter */}
        <Select value={sourceFilter || '__all__'} onValueChange={(v: string) => setSourceFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Sources</SelectItem>
            {uniqueSources.map((source) => (
              <SelectItem key={source} value={source}>
                {source}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Event type filter */}
        <Input
          placeholder="Filter by type (e.g. agent.run.*)"
          value={eventTypeFilter}
          onChange={(e) => setEventTypeFilter(e.target.value)}
          className="w-[260px]"
        />

        {/* Clear */}
        <Button variant="outline" onClick={clearEvents}>
          Clear
        </Button>

        {/* Connection indicator */}
        <ConnectionIndicator isConnected={isConnected} diagnostics={diagnostics} className="ml-auto" />
      </div>

      {/* Exclude chips + Group mode */}
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        {EXCLUDE_CHIPS.map((chip) => {
          const excluded = chip.patterns.every((p) => excludedTypes.has(p));
          return (
            <button
              key={chip.label}
              type="button"
              data-testid={`exclude-chip-${chip.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => toggleExclude(chip.patterns)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                excluded
                  ? 'bg-muted text-muted-foreground border-muted line-through'
                  : 'bg-background text-foreground border-border hover:bg-accent'
              }`}
            >
              {excluded ? (
                <EyeOff className="h-3 w-3" />
              ) : (
                <Eye className="h-3 w-3" />
              )}
              {chip.label}
            </button>
          );
        })}

        <div className="h-4 w-px bg-border" />

        <div className="flex items-center gap-2">
          <label htmlFor="group-mode-select" className="text-xs font-medium whitespace-nowrap">
            Group by
          </label>
          <Select
            value={groupMode}
            onValueChange={(v: string) => setGroupMode(v as GroupMode)}
          >
            <SelectTrigger
              id="group-mode-select"
              className="h-7 w-[130px] text-xs"
              data-testid="group-mode-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="run">Run</SelectItem>
              <SelectItem value="source">Source</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Event count */}
      <p className="text-xs text-muted-foreground mb-2">
        Showing {items.length} item{items.length !== 1 ? 's' : ''} ({totalEvents} event
        {totalEvents !== 1 ? 's' : ''} across {groupCount} group{groupCount !== 1 ? 's' : ''}, max{' '}
        {maxGroupedItems} groups)
      </p>

      {/* Event list */}
      <div className="flex-1 overflow-y-auto min-h-0 space-y-2">
        {items.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
            No events captured yet. Events will appear here as they stream in.
          </div>
        ) : (
          items.map((item) => {
            if (item.kind === 'group') {
              return <RunGroupCard key={item.subjectId} group={item} />;
            }
            if (item.kind === 'source-group') {
              return <SourceGroupCard key={item.source} group={item} />;
            }
            return <EventCard key={item.event.eventId} event={item.event} />;
          })
        )}
      </div>
    </div>
  );
}
